import React from "react";
import { getSplitTreatmentsInGetInitialProps } from "@leafly-com/split-next";
import { NextPage } from "next";

import getRegionComplianceRules from "api/requests/consumerApi/getRegionComplianceRules";
import { getDispensary } from "api/requests/getDispensary";
import { getDispensaryDeals } from "api/requests/getDispensaryDeals";
import { DISPENSARY_PAGES } from "constants/dispensary";
import { Deal } from "custom-types/Deals";
import { Dispensary } from "custom-types/Dispensary";
import { COMPLIANCE_RULESET_LOAD } from "redux/reducers/complianceRules";
import { getDomainCountryCode } from "redux/selectors/config";
import { getSplitKey } from "utils/split/getSplitKey";
import useTrackDispensaryPageViewSplitEvent from "utils/split/useTrackDispensaryPageViewSplitEvent";
import { validateDispensary } from "utils/validateDispensary";

import DealsDisclaimer from "components/Dispensary/DealsDisclaimer";
import DealsSection from "components/Dispensary/DealsSection";
import DispensaryHead from "components/Dispensary/DispensaryHead";
import DispensaryHeaderSlim from "components/Dispensary/DispensaryHeaderSlim/DispensaryHeaderSlim";
import DispensaryTabs from "components/Dispensary/DispensaryTabs";
import PromotionsSection from "components/Dispensary/PromotionsSection";

import DispensaryBreadcrumbs from "../../../components/Dispensary/DispensaryBreadcrumbs";

type DealsPageProps = {
  deals: Deal[];
  dispensary: Dispensary;
  showMoreDeals: boolean;
};

const DealsPage: NextPage<DealsPageProps> = ({ deals, dispensary }) => {
  useTrackDispensaryPageViewSplitEvent({
    page: "deals",
    tier: dispensary.featureTier,
  });

  return (
    <>
      <DispensaryHead dispensary={dispensary} pageType="deals" />
      <DispensaryHeaderSlim dispensary={dispensary} />
      <DispensaryTabs
        activeTab={DISPENSARY_PAGES.deals}
        dispensary={dispensary}
      />

      <div data-testid="deals__content" className="container py-xxl">
        <DealsSection deals={deals} dispensary={dispensary} />
        <PromotionsSection />
        <DealsDisclaimer />
        <DispensaryBreadcrumbs dispensary={dispensary} />
      </div>
    </>
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: fix me please, do not replicate
DealsPage.getInitialProps = async (context): Promise<any> => {
  const { req, res, query, store } = context;
  const { slug } = query;

  const countryCode = getDomainCountryCode(store.getState());

  try {
    const dispensary = await getDispensary(slug as string);

    const { redirect } = await validateDispensary({
      countryCode,
      dispensary,
      query,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- temporarily using non-null assertion before refactoring validateDispensary
      req: req!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- temporarily using non-null assertion before refactoring validateDispensary
      res: res!,
    });

    if (redirect) {
      return;
    }

    const {
      "NL-1191_setDealsCapTo50_flag": { treatment: setDealsCap },
    } = await getSplitTreatmentsInGetInitialProps(
      ["NL-1191_setDealsCapTo50_flag"],
      getSplitKey(context),
    );

    const showMoreDeals = setDealsCap === "on";

    const dealsTake = showMoreDeals ? 60 : 20;

    if (dispensary) {
      store.dispatch({
        dispensary,
        type: "dispensary/load",
      });

      const [deals, complianceRuleset] = await Promise.all([
        getDispensaryDeals(String(slug), dealsTake),
        getRegionComplianceRules(dispensary?.state, dispensary?.country),
      ]);

      if (complianceRuleset) {
        store.dispatch({
          complianceRuleset,
          type: COMPLIANCE_RULESET_LOAD,
        });
      }

      return {
        deals,
        dispensary,
      };
    }
  } catch (error) {
    return Promise.resolve({
      dispensary: { error: error.message, slug: query.slug },
    });
  }
};

export default DealsPage;
